import { Fragment } from 'react';
import { FormattedMessage, FormattedDate, defineMessages } from 'react-intl';
import FormattedDuration from 'react-intl-formatted-duration';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import parseISO from 'date-fns/parseISO';
import {
  Text,
  Table,
  Tr,
  Td,
  Th,
  rhythms,
  Flex
} from '@urbaninfrastructure/react-ui-kit';
import { orderQuery_order, orderQuery_order_trip } from '../../core-types';
import { FormattedCurrency } from '../../components/FormattedCurrency';
import { DiscountAmount } from '../../components/DiscountAmount';
import { globalMessages } from '../../lib/messages';

defineMessages({
  'react-intl-formatted-duration.daysUnit': {
    id: 'react-intl-formatted-duration.daysUnit',
    defaultMessage: '{value, plural, one {day} other {days}}'
  },
  'react-intl-formatted-duration.hoursUnit': {
    id: 'react-intl-formatted-duration.hoursUnit',
    defaultMessage: '{value, plural, one {hour} other {hours}}'
  },
  'react-intl-formatted-duration.minutesUnit': {
    id: 'react-intl-formatted-duration.minutesUnit',
    defaultMessage: '{value, plural, one {min} other {min}}'
  },
  'react-intl-formatted-duration.secondsUnit': {
    id: 'react-intl-formatted-duration.secondsUnit',
    defaultMessage: '{value, plural, one {second} other {seconds}}'
  }
});

const messages = defineMessages({
  productName: {
    id: 'modules.Receipt.orderTypes.productName',
    defaultMessage: 'Product'
  },
  totalAmount: {
    id: 'total',
    defaultMessage: 'Total'
  },
  amountWithoutVat: {
    id: 'modules.Receipt.orderTypes.amountWithoutVat',
    defaultMessage: 'Amount w/o VAT'
  },
  totalIncludingVAT: {
    id: 'modules.Receipt.orderTypes.totalIncludingVAT',
    defaultMessage: 'Total including VAT'
  },
  vat: {
    id: 'modules.Receipt.orderTypes.vat',
    defaultMessage: 'VAT'
  },
  fromStation: {
    id: 'modules.Receipt.orderTypes.fromStation',
    defaultMessage: 'From {station} ({date})'
  },
  toStation: {
    id: 'modules.Receipt.orderTypes.toStation',
    defaultMessage: 'To {station} ({date})'
  },
  startLocation: {
    id: 'trip_summary_pick_up',
    defaultMessage: 'Pick up'
  },
  endLocation: {
    id: 'trip_summary_drop_off',
    defaultMessage: 'Drop off'
  },
  totalTime: {
    id: 'modules.Receipt.orderTypes.totalTime',
    defaultMessage: 'Total time: {totalTime}'
  },
  includedTime: {
    id: 'modules.Receipt.orderTypes.includedTime',
    defaultMessage: 'Included {includedMinutes} min:'
  },
  walletDeposit: {
    id: 'modules.Profile.Receipts.table.type.walletDeposit',
    defaultMessage: 'Wallet Deposit:'
  }
});

type Props = {
  order: orderQuery_order;
};

const fractionDigits = 2;
// const fractionDigits = vatAmount % 1 === 0 ? 0 : 2

const longDateProps: {
  year: 'numeric' | '2-digit' | undefined;
  month: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow' | undefined;
  day: 'numeric' | '2-digit' | undefined;
  hour: 'numeric' | '2-digit' | undefined;
  minute: 'numeric' | '2-digit' | undefined;
} = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric'
};

const TripStartLocation = ({ trip }: { trip: orderQuery_order_trip }) => {
  if (!trip) {
    return null;
  }
  if (trip.startLocation) {
    return (
      <Flex alignItems="center">
        <Text small mr={2}>
          <FormattedMessage {...messages.startLocation} />
        </Text>
        {trip.startedAt && (
          <Text small>
            (<FormattedDate value={trip.startedAt} {...longDateProps} />)
          </Text>
        )}
      </Flex>
    );
  }
  if (trip.startDock) {
    return (
      <FormattedMessage
        {...messages.fromStation}
        values={{
          station:
            trip.startDock.dockGroup.title || trip.startDock.dockGroup.name,
          date: trip.startedAt && (
            <FormattedDate value={trip.startedAt} {...longDateProps} />
          )
        }}
      />
    );
  }
  return null;
};

const TripEndLocation = ({ trip }: { trip: orderQuery_order_trip }) => {
  if (!trip) {
    return null;
  }
  if (trip.endLocation) {
    return (
      <Flex alignItems="center">
        <Text small mr={2}>
          <FormattedMessage {...messages.endLocation} />
        </Text>
        {trip.endedAt && (
          <Text small>
            (<FormattedDate value={trip.endedAt} {...longDateProps} />)
          </Text>
        )}
      </Flex>
    );
  }
  if (trip.endDock) {
    return (
      <FormattedMessage
        {...messages.toStation}
        values={{
          station: trip.endDock.dockGroup.title || trip.endDock.dockGroup.name,
          date: trip.endedAt && (
            <FormattedDate value={trip.endedAt} {...longDateProps} />
          )
        }}
      />
    );
  }
  return null;
};

function Subscription({ order, ...props }: Props) {
  const vatAmount =
    (order.discount
      ? order.discount.totalVatAmountDiscounted
      : order.vatAmount) || 0;
  const amount = order.discount
    ? order.discount.totalAmountDiscounted
    : order.amount || 0;

  return (
    <Table {...props}>
      <tbody>
        {order.orderLines.map(orderLine => {
          if (!orderLine) {
            return null;
          }
          const productName = orderLine.product && orderLine.product.name;
          const isRegistrationFee = orderLine.type === 'registration_fee';
          return (
            <Fragment key={orderLine.id}>
              <Tr>
                <Td borderWidth={orderLine.discount ? '0' : undefined}>
                  {isRegistrationFee ? (
                    <Text mb="xs">
                      <FormattedMessage
                        id="modules.Receipt.RegistrationFee.subHeading"
                        defaultMessage="Registration fee"
                      />
                    </Text>
                  ) : (
                    `${productName} × ${orderLine.quantity}`
                  )}
                </Td>
                <Td
                  textAlign="right"
                  borderWidth={orderLine.discount ? '0' : undefined}
                >
                  <FormattedCurrency
                    value={orderLine.totalAmount}
                    precision={fractionDigits}
                  />
                </Td>
              </Tr>
              {orderLine.discount && (
                <Tr>
                  <Td py={0} pb={rhythms.xxs}>
                    <FormattedMessage id="discount" defaultMessage="Discount" />{' '}
                    <em>{orderLine.discount.name}</em> ×{' '}
                    {orderLine.discount.quantityDiscounted}{' '}
                    {orderLine.discount.discountAmount.__typename ===
                      'RelativeDiscountAmount' && (
                      <DiscountAmount
                        discountAmount={orderLine.discount.discountAmount}
                      />
                    )}
                  </Td>
                  <Td py={0} pb={rhythms.xxs} textAlign="right">
                    <FormattedCurrency
                      value={orderLine.discount.totalDiscountAmount}
                      precision={fractionDigits}
                    />
                  </Td>
                </Tr>
              )}
            </Fragment>
          );
        })}
        <Tr>
          <Td>
            <Text bold>
              <FormattedMessage {...messages.totalAmount} />
            </Text>
          </Td>
          <Td textAlign="right">
            <Text bold data-testid="OrderTable__OrderAmount">
              <FormattedCurrency value={amount} precision={fractionDigits} />
            </Text>
          </Td>
        </Tr>
      </tbody>
      <tfoot>
        <Tr>
          <Td>
            <Text mute small>
              <FormattedMessage {...messages.vat} />
            </Text>
          </Td>
          <Td textAlign="right">
            <Text mute small>
              <FormattedCurrency value={vatAmount} precision={fractionDigits} />
            </Text>
          </Td>
        </Tr>
      </tfoot>
    </Table>
  );
}

function Reservation({ order, ...props }: Props) {
  return (
    <Table {...props}>
      <tbody>
        {order.orderLines.map(orderLine => {
          if (!orderLine) {
            return null;
          }

          return (
            <Fragment key={orderLine.id}>
              <Tr>
                <Td>
                  <Text mb="xs">
                    <FormattedMessage
                      id="modules.Receipt.orderTypes.reservation"
                      defaultMessage="Reservation"
                    />
                  </Text>
                </Td>
                <Td textAlign="right">
                  <FormattedCurrency
                    value={orderLine.totalAmount}
                    precision={fractionDigits}
                  />
                </Td>
              </Tr>
            </Fragment>
          );
        })}
        <Tr>
          <Td>
            <Text bold>
              <FormattedMessage {...messages.totalAmount} />
            </Text>
          </Td>
          <Td textAlign="right">
            <Text bold data-testid="OrderReservationTable__OrderAmount">
              <FormattedCurrency
                value={order.amount || 0}
                precision={fractionDigits}
              />
            </Text>
          </Td>
        </Tr>
      </tbody>
      <tfoot>
        <Tr>
          <Td>
            <Text mute small>
              <FormattedMessage {...messages.vat} />
            </Text>
          </Td>
          <Td textAlign="right">
            <Text mute small>
              <FormattedCurrency
                value={order.vatAmount || 0}
                precision={fractionDigits}
              />
            </Text>
          </Td>
        </Tr>
      </tfoot>
    </Table>
  );
}

function ExtendedRental({ order }: Props) {
  const vatAmount = order.vatAmount || 0;
  const trip = order && order.trip;
  const totalTime = (trip && trip.duration) || 0;
  const freePeriodMins = (trip && trip.freePeriodMins) || 0;
  const paidPeriodMins = (trip && trip.paidPeriodMins) || 0;

  return (
    <>
      {trip && (
        <Table mb="sm">
          <tbody>
            <Tr>
              <Td textAlign="left" colSpan={2}>
                <TripStartLocation trip={trip} />
              </Td>
            </Tr>
            <Tr>
              <Td textAlign="left" colSpan={2}>
                <TripEndLocation trip={trip} />
              </Td>
            </Tr>
            <Tr>
              <Td textAlign="right" colSpan={2}>
                {totalTime && (
                  <FormattedMessage
                    {...messages.totalTime}
                    values={{
                      totalTime: (
                        <FormattedDuration
                          format="{days} {hours} {minutes} {seconds}"
                          seconds={totalTime}
                        />
                      )
                    }}
                  />
                )}
              </Td>
            </Tr>
          </tbody>
        </Table>
      )}
      <Table>
        <tbody>
          <Tr>
            <Td>
              <FormattedMessage
                {...messages.includedTime}
                values={{ includedMinutes: freePeriodMins }}
              />
            </Td>
            <Td textAlign="right">
              <FormattedCurrency value={0} precision={fractionDigits} />
            </Td>
          </Tr>
          <Tr>
            <Td>
              {paidPeriodMins && (
                <>
                  <FormattedMessage {...globalMessages.extendedRental} />{' '}
                  <FormattedDuration
                    format="{days} {hours} {minutes} {seconds}"
                    seconds={paidPeriodMins * 60}
                  />
                  :
                </>
              )}
            </Td>
            <Td textAlign="right">
              {order.amount && (
                <FormattedCurrency
                  value={order.amount}
                  precision={fractionDigits}
                />
              )}
            </Td>
          </Tr>

          <Tr>
            <Td>
              <Text bold>
                <FormattedMessage {...messages.totalAmount} />
              </Text>
            </Td>
            <Td textAlign="right">
              <Text bold data-testid="OrderTable__OrderAmount">
                {order.amount && (
                  <FormattedCurrency
                    value={order.amount}
                    precision={fractionDigits}
                  />
                )}
              </Text>
            </Td>
          </Tr>
        </tbody>
        <tfoot>
          <Tr>
            <Td>
              <Text mute small>
                <FormattedMessage {...messages.vat} />
              </Text>
            </Td>
            <Td textAlign="right">
              <Text mute small>
                <FormattedCurrency
                  value={vatAmount}
                  precision={fractionDigits}
                />
              </Text>
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  );
}

function Trip({ order }: Props) {
  const vatAmount = order.vatAmount || 0;
  const trip = order && order.trip;
  const totalTime = (trip && trip.duration) || 0;

  return (
    <>
      <Table>
        <tbody>
          {trip && (
            <>
              <Tr>
                <Td textAlign="left" colSpan={2}>
                  <TripStartLocation trip={trip} />
                </Td>
              </Tr>
              <Tr>
                <Td textAlign="left" colSpan={2}>
                  <TripEndLocation trip={trip} />
                </Td>
              </Tr>
              <Tr>
                <Td textAlign="right" colSpan={2}>
                  {totalTime && (
                    <FormattedMessage
                      {...messages.totalTime}
                      values={{
                        totalTime: (
                          <FormattedDuration
                            format="{days} {hours} {minutes} {seconds}"
                            seconds={totalTime}
                          />
                        )
                      }}
                    />
                  )}
                </Td>
              </Tr>
            </>
          )}
          {order.orderLines &&
            order.orderLines.map(orderLine => {
              const isFreeMinutes = orderLine?.type === 'free_minutes_per_day';
              if (!orderLine) {
                return null;
              }
              return (
                <Fragment key={orderLine.id}>
                  <Tr>
                    <Td colSpan={1}>
                      <Text>{orderLine.orderLineTitle}</Text>
                    </Td>
                    <Td textAlign="right">
                      {isFreeMinutes
                        ? orderLine.amount || 0
                        : orderLine.totalAmount && (
                            <FormattedCurrency
                              value={orderLine.totalAmount}
                              precision={fractionDigits}
                            />
                          )}
                    </Td>
                  </Tr>
                </Fragment>
              );
            })}
          <Tr>
            <Td colSpan={1}>
              <Text fontWeight="bold">
                <FormattedMessage {...messages.totalAmount} />
              </Text>
            </Td>
            <Td textAlign="right">
              {order.amount && (
                <FormattedCurrency
                  value={order.amount}
                  precision={fractionDigits}
                />
              )}
            </Td>
          </Tr>
        </tbody>
        <tfoot>
          <Tr>
            <Td>
              <Text mute small>
                <FormattedMessage {...messages.vat} />
              </Text>
            </Td>
            <Td textAlign="right">
              <Text mute small>
                <FormattedCurrency
                  value={vatAmount}
                  precision={fractionDigits}
                />
              </Text>
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  );
}

function Pay2Go({ order }: Props) {
  const vatAmount = order.vatAmount || 0;

  const trip = order.trip;
  const startedAt = trip && trip.startedAt && parseISO(trip.startedAt);
  const endedAt = trip && trip.endedAt && parseISO(trip.endedAt);

  const totalTime =
    endedAt && startedAt && differenceInSeconds(endedAt, startedAt);

  return (
    <>
      <Table mb="sm">
        <tbody>
          <Tr>
            <Td colSpan={2}>
              {trip &&
                trip.startDock &&
                trip.startDock.dockGroup &&
                trip.startedAt && (
                  <FormattedMessage
                    {...messages.fromStation}
                    values={{
                      station:
                        trip.startDock.dockGroup.title ||
                        trip.startDock.dockGroup.name,
                      date: (
                        <FormattedDate
                          value={trip.startedAt}
                          {...longDateProps}
                        />
                      )
                    }}
                  />
                )}
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={2}>
              {trip && trip.endDock && trip.endDock.dockGroup && trip.endedAt && (
                <FormattedMessage
                  {...messages.toStation}
                  values={{
                    station:
                      trip.endDock.dockGroup.title ||
                      trip.endDock.dockGroup.name,
                    date: (
                      <FormattedDate value={trip.endedAt} {...longDateProps} />
                    )
                  }}
                />
              )}
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={2} textAlign="right">
              <FormattedMessage
                {...messages.totalTime}
                values={{
                  totalTime: (
                    <FormattedDuration
                      format="{days} {hours} {minutes} {seconds}"
                      seconds={totalTime}
                    />
                  )
                }}
              />
            </Td>
          </Tr>
        </tbody>
      </Table>
      <Table>
        <tbody>
          <Tr>
            <Td>
              <FormattedMessage
                id="modules.Receipt.orderTypes.tripDuration"
                defaultMessage="Cost of {tripDuration}"
                values={{
                  tripDuration: (
                    <FormattedDuration
                      format="{days} {hours} {minutes} {seconds}"
                      seconds={totalTime}
                    />
                  )
                }}
              />
            </Td>
            <Td textAlign="right">
              {order.amount !== null && order.amount !== undefined && (
                <FormattedCurrency
                  value={order.amount}
                  precision={fractionDigits}
                />
              )}
            </Td>
          </Tr>
          <Tr>
            <Td>
              <Text bold>
                <FormattedMessage {...messages.totalAmount} />
              </Text>
            </Td>
            <Td textAlign="right">
              <Text bold>
                {order.amount && (
                  <FormattedCurrency
                    value={order.amount}
                    precision={fractionDigits}
                  />
                )}
              </Text>
            </Td>
          </Tr>
        </tbody>
        <tfoot>
          <Tr>
            <Td>
              <Text mute small>
                <FormattedMessage {...messages.vat} />
              </Text>
            </Td>
            <Td textAlign="right">
              <Text mute small>
                <FormattedCurrency
                  value={vatAmount}
                  precision={fractionDigits}
                />
              </Text>
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  );
}

function BusinessRegularCharge({ order }: Props) {
  const orderLines = order.orderLines || [];
  const numberOfValueCodes = orderLines.reduce((accum, orderLine) => {
    if (!orderLine || !orderLine.valueCodes) {
      return accum;
    }
    return accum + orderLine.valueCodes.length;
  }, 0);
  return (
    <>
      <Text mb="xs">
        {order.completedAt && (
          <Text fontWeight="bold">
            <FormattedMessage
              id="modules.Receipt.orderTypes.subHeading"
              values={{
                numberOfValueCodes,
                month: (
                  <FormattedDate
                    value={order.completedAt}
                    year="numeric"
                    month="long"
                  />
                )
              }}
            />
          </Text>
        )}
      </Text>
      <Table variant="simple" mb="sm">
        <thead>
          <Tr>
            <Th>
              <FormattedMessage {...messages.productName} />
            </Th>
            <Th>
              <FormattedMessage
                id="modules.Receipt.orderTypes.table.code"
                defaultMessage="Code"
              />
            </Th>
            <Th>
              <FormattedMessage
                id="modules.Receipt.orderTypes.table.redeemedDate"
                defaultMessage="Redeemed date"
              />
            </Th>
            <Th textAlign="right">
              <FormattedMessage {...messages.amountWithoutVat} />
            </Th>
          </Tr>
        </thead>
        <tbody>
          {order.orderLines &&
            order.orderLines.map(orderLine => {
              if (!orderLine) {
                return null;
              }
              return (
                <Fragment key={orderLine.id}>
                  <Tr>
                    <Td colSpan={3}>
                      <Text fontWeight="bold">
                        {orderLine.product && orderLine.product.name}
                      </Text>
                    </Td>
                    <Td textAlign="right">
                      {orderLine.amountWithoutVat && (
                        <FormattedCurrency value={orderLine.amountWithoutVat} />
                      )}
                    </Td>
                  </Tr>
                  {orderLine.valueCodes &&
                    orderLine.valueCodes.map(valueCode => {
                      if (!valueCode) {
                        return null;
                      }
                      return (
                        <Tr key={valueCode.code}>
                          <Td />
                          <Td>{valueCode.code}</Td>
                          <Td colSpan={2}>
                            {valueCode.redeemedAt && (
                              <FormattedDate
                                value={valueCode.redeemedAt}
                                year="numeric"
                                month="long"
                                day="2-digit"
                                hour="numeric"
                                minute="numeric"
                              />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                </Fragment>
              );
            })}
        </tbody>
        <tfoot>
          <Tr>
            <Td colSpan={3}>
              <Text fontSize={2} fontWeight="bold">
                <FormattedMessage {...messages.totalAmount} />
              </Text>
            </Td>
            <Td textAlign="right">
              {order.amountWithoutVat && (
                <Text fontSize={2} fontWeight="bold">
                  <FormattedCurrency value={order.amountWithoutVat} />
                </Text>
              )}
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={3} py={0}>
              <FormattedMessage {...messages.vat} />
            </Td>
            <Td py={0} textAlign="right">
              {order.vatAmount && <FormattedCurrency value={order.vatAmount} />}
            </Td>
          </Tr>
          <Tr>
            <Td py={0} colSpan={3}>
              <FormattedMessage {...messages.totalIncludingVAT} />
            </Td>
            <Td py={0} textAlign="right">
              {order.amount && <FormattedCurrency value={order.amount} />}
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  );
}

function BusinessRegistrationFee({ order }: Props) {
  return (
    <>
      <Text mb="xs" fontWeight="bold">
        <FormattedMessage
          id="modules.Receipt.RegistrationFee.subHeading"
          defaultMessage="Registration fee"
        />
      </Text>
      <Table variant="simple" mb="sm">
        <thead>
          <Tr>
            <Th>
              <FormattedMessage {...messages.productName} />
            </Th>
            <Th textAlign="right">
              <FormattedMessage {...messages.amountWithoutVat} />
            </Th>
          </Tr>
        </thead>
        <tbody>
          {order.orderLines &&
            order.orderLines.map(orderLine => {
              if (!orderLine) {
                return null;
              }
              return (
                <Tr key={orderLine.id}>
                  <Td>
                    <Text fontWeight="bold">
                      {orderLine.product && orderLine.product.name} ×{' '}
                      {orderLine.valueCodes && orderLine.valueCodes.length}
                    </Text>
                  </Td>
                  <Td textAlign="right">
                    {orderLine.amountWithoutVat && (
                      <FormattedCurrency value={orderLine.amountWithoutVat} />
                    )}
                  </Td>
                </Tr>
              );
            })}
        </tbody>
        <tfoot>
          <Tr data-testid="ReceiptTable__Total">
            <Td>
              <Text fontSize={2} fontWeight="bold">
                <FormattedMessage {...messages.totalAmount} />
              </Text>
            </Td>
            <Td textAlign="right">
              {order.amountWithoutVat && (
                <Text fontSize={2} fontWeight="bold">
                  <FormattedCurrency value={order.amountWithoutVat} />
                </Text>
              )}
            </Td>
          </Tr>
          <Tr data-testid="ReceiptTable__VatAmount">
            <Td py={0}>
              <FormattedMessage {...messages.vat} />
            </Td>
            <Td py={0} textAlign="right">
              {order.vatAmount && <FormattedCurrency value={order.vatAmount} />}
            </Td>
          </Tr>
          <Tr data-testid="ReceiptTable__TotalIncludingVat">
            <Td py={0}>
              <FormattedMessage {...messages.totalIncludingVAT} />
            </Td>
            <Td py={0} textAlign="right">
              {order.amount && <FormattedCurrency value={order.amount} />}
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  );
}

function WalletDeposit({ order }: Props) {
  return (
    <>
      <Table>
        <tbody>
          <Tr>
            <Td textAlign="left" colSpan={1}>
              <FormattedMessage {...messages.walletDeposit} />
            </Td>
            <Td textAlign="right">
              {order.amount && (
                <FormattedCurrency
                  value={order.amount}
                  precision={fractionDigits}
                />
              )}
            </Td>
          </Tr>
          <Tr>
            <Td colSpan={1}>
              <Text fontWeight="bold">
                <FormattedMessage {...messages.totalAmount} />
              </Text>
            </Td>
            <Td textAlign="right">
              {order.amount && (
                <FormattedCurrency
                  value={order.amount}
                  precision={fractionDigits}
                />
              )}
            </Td>
          </Tr>
        </tbody>
        <tfoot>
          <Tr>
            <Td textAlign="right">
              <Text mute small></Text>
            </Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  );
}

export default {
  subscription: Subscription,
  reservation: Reservation,
  value_code: Subscription,
  trip: Trip,
  extended_rental: ExtendedRental,
  pay2go: Pay2Go,
  business_registration_fee: BusinessRegistrationFee,
  business_regular_charge: BusinessRegularCharge,
  wallet_deposit: WalletDeposit,
  verification: () => {
    throw new Error('Order type verification not implemented');
  }
};
